import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The console's button.
 *
 * `default` is the one primary action on a screen — used more than once, it stops meaning
 * anything. `outline` is the workhorse. `danger` is reserved for destructive confirmation, not
 * for every delete trigger (those are `ghost` with danger text inside a menu).
 */
const buttonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md",
    "font-medium transition-[background-color,border-color,color,box-shadow] duration-100",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        default:
          "bg-accent-9 text-white shadow-xs hover:bg-accent-10 active:bg-accent-11 " +
          "dark:text-gray-1",
        outline:
          "border border-border bg-surface text-text shadow-xs hover:bg-surface-hover " +
          "active:bg-gray-4",
        ghost: "text-text-muted hover:bg-surface-hover hover:text-text",
        danger:
          "bg-danger-9 text-white shadow-xs hover:brightness-110 active:brightness-95",
        subtle: "bg-gray-3 text-text hover:bg-gray-4 active:bg-gray-5",
        link: "text-accent-11 underline-offset-4 hover:underline",
      },
      size: {
        // 32px: dense enough to sit in a table row, still a comfortable click target.
        sm: "h-7 px-2.5 text-xs [&_svg]:size-3.5",
        md: "h-8 px-3 text-sm [&_svg]:size-4",
        lg: "h-9 px-4 text-base [&_svg]:size-4",
        icon: "size-8 [&_svg]:size-4",
        "icon-sm": "size-7 [&_svg]:size-3.5",
      },
    },
    defaultVariants: { variant: "outline", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export function Button({
  className, variant, size, asChild = false, loading = false, disabled, children, ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      // Tells assistive tech the control is working; a spinner alone is invisible to it.
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { buttonVariants };
